import { useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { fmtTime } from '../../shared/dates';
import { Icon } from '../../ui/Icon';
import { ProviderForm } from './ProviderForm';

type Tab = 'census' | 'emr';

export function ProvidersPage() {
  const queryClient = useQueryClient();
  const gridRef = useRef<AgGridReact | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [tab, setTab] = useState<Tab>('census');
  const [search, setSearch] = useState('');

  const { data: providers, isLoading } = useQuery({
    queryKey: ['providers', showInactive],
    queryFn: () =>
      api
        .get('/providers', { params: showInactive ? {} : { activeOnly: 'true' } })
        .then((r) => r.data),
  });

  const { data: emrProviders, isLoading: emrLoading } = useQuery({
    queryKey: ['providers-emr'],
    queryFn: () => api.get('/providers/emr').then((r) => r.data),
  });

  const importedExternalRefs = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    for (const p of providers ?? []) {
      if (p.externalRef) set.add(p.externalRef);
    }
    return set;
  }, [providers]);

  const [importFilter, setImportFilter] = useState<'all' | 'imported' | 'not-imported'>('all');

  const filteredEmrProviders = useMemo(() => {
    return (emrProviders ?? [])
      .filter((p: any) => {
        if (!p.active) return false;
        const isImported = importedExternalRefs.has(`emr-${p.id}`);
        if (importFilter === 'imported') return isImported;
        if (importFilter === 'not-imported') return !isImported;
        return true;
      })
      .sort((a: any, b: any) => {
        const aImported = importedExternalRefs.has(`emr-${a.id}`);
        const bImported = importedExternalRefs.has(`emr-${b.id}`);
        if (aImported !== bImported) return aImported ? 1 : -1;
        return 0;
      });
  }, [emrProviders, importedExternalRefs, importFilter]);

  const [selectedCount, setSelectedCount] = useState(0);

  const importEmr = useMutation({
    mutationFn: (ids: number[]) => api.post('/providers/import-emr', { ids }).then((r) => r.data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      toast.success(`Imported ${result.synced} providers (${result.created} created, ${result.updated} updated)`);
      gridRef.current?.api.deselectAll();
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Import failed'),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: any }) =>
      api.patch(`/providers/${id}`, patch).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      toast.success('Provider updated');
    },
  });

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/providers/${id}`, { active }).then((r) => r.data),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      toast.success(vars.active ? 'Provider reactivated' : 'Provider deactivated');
    },
  });

  const columnDefs = useMemo<ColDef[]>(
    () => [
      { headerName: 'First name', field: 'firstName', editable: true, width: 130, pinned: 'left' },
      { headerName: 'Last name', field: 'lastName', editable: true, width: 130, pinned: 'left' },
      { headerName: 'Role', field: 'displayRole', width: 180 },
      { headerName: 'Email', field: 'email', editable: true, width: 220 },
      {
        headerName: 'Services',
        valueGetter: (p) => p.data?.servicesLabel ?? (p.data?.serviceTypes ?? []).join(', '),
        width: 280,
        tooltipValueGetter: (p) =>
          p.data?.servicesLabel ?? (p.data?.serviceTypes ?? []).join(', '),
      },
      {
        headerName: 'Place',
        valueGetter: (p) =>
          p.data?.placeLabel ??
          (p.data?.placeOfService === 'HOME_COMMUNITY' ? 'Home / Community' : 'Office'),
        width: 140,
      },
      {
        headerName: 'Location',
        valueGetter: (p) => p.data?.locationCity ?? 'N/A',
        width: 130,
      },
      {
        headerName: 'Telehealth',
        valueGetter: (p) => (p.data?.telehealthCapable ? 'Yes' : 'No'),
        width: 110,
      },
      {
        headerName: 'Cell',
        valueGetter: (p) => p.data?.cellPhone ?? '',
        width: 130,
      },
      { headerName: 'Weekly cap (hrs)', field: 'maxDirectHoursPerWeek', editable: true, width: 140 },
      {
        headerName: 'Availability',
        valueGetter: (p) =>
          (p.data?.availability ?? [])
            .map((a: any) => `${a.day} ${fmtTime(a.startMin)}–${fmtTime(a.endMin)}`)
            .join(', '),
        flex: 1,
        minWidth: 220,
      },
      {
        headerName: 'Status',
        field: 'active',
        width: 110,
        cellRenderer: (p: any) => (
          <span className={p.value ? 'pill-success' : 'pill-muted'}>
            {p.value ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        headerName: 'Actions',
        pinned: 'right',
        width: 280,
        sortable: false,
        cellRenderer: (p: any) => (
          <div className="flex h-full items-center gap-3 text-xs">
            <Link className="text-brand-700 hover:underline" to={`/providers/${p.data.id}`}>
              Open
            </Link>
            <button
              className="text-brand-700 hover:underline"
              onClick={() => {
                setEditing(p.data);
                setFormOpen(true);
              }}
            >
              Edit
            </button>
            <Link
              className="text-brand-700 hover:underline"
              to={`/print/provider/${p.data.id}`}
              target="_blank"
            >
              Print
            </Link>
            {p.data.active ? (
              <button
                className="text-rose-600 hover:underline"
                onClick={() => {
                  if (confirm(`Deactivate ${p.data.firstName} ${p.data.lastName}?`)) {
                    setActive.mutate({ id: p.data.id, active: false });
                  }
                }}
              >
                Deactivate
              </button>
            ) : (
              <button
                className="text-emerald-700 hover:underline"
                onClick={() => setActive.mutate({ id: p.data.id, active: true })}
              >
                Reactivate
              </button>
            )}
          </div>
        ),
      },
    ],
    [setActive],
  );

  const emrColumnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: '',
        checkboxSelection: (p: any) => !importedExternalRefs.has(`emr-${p.data?.id}`),
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        width: 50,
        pinned: 'left',
        sortable: false,
        filter: false,
        resizable: false,
      },
      { headerName: 'First name', field: 'firstName', width: 130, pinned: 'left' },
      { headerName: 'Last name', field: 'lastName', width: 130, pinned: 'left' },
      { headerName: 'Role', field: 'role', width: 180 },
      { headerName: 'Email', field: 'email', width: 220 },
      { headerName: 'Services', field: 'services', width: 280, tooltipField: 'services' },
      { headerName: 'Place', field: 'placeOfService', width: 200 },
      {
        headerName: 'Telehealth',
        field: 'telehealthCapable',
        width: 110,
        cellRenderer: (p: any) => (p.value ? 'Yes' : 'No'),
      },
      { headerName: 'Cell', field: 'phone', width: 140 },
      {
        headerName: 'Weekly cap (hrs)',
        field: 'expectedProductivity',
        width: 140,
        valueGetter: (p: any) => p.data?.expectedProductivity ?? '',
      },
      {
        headerName: 'Availability',
        valueGetter: () => '',
        width: 220,
      },
      {
        headerName: 'Status',
        field: 'active',
        width: 110,
        cellRenderer: (p: any) => (
          <span className={p.value ? 'pill-success' : 'pill-muted'}>
            {p.value ? 'Active' : 'Inactive'}
          </span>
        ),
      },
    ],
    [importedExternalRefs],
  );

  function onCellValueChanged(e: any) {
    if (!e.colDef.editable) return;
    update.mutate({ id: e.data.id, patch: { [e.colDef.field!]: e.newValue } });
  }

  const isEmr = tab === 'emr';

  return (
    <div className="flex h-full flex-col gap-4">
      {importEmr.isPending && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-black/40 backdrop-blur-sm">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-white border-t-transparent" />
          <p className="text-lg font-semibold text-white">Importing providers…</p>
        </div>
      )}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">Providers</h1>
          <p className="text-sm text-ink-subtle">
            {isEmr
              ? 'Read-only view of providers from the EMR database.'
              : 'Inline-edit basics here. Open a row for availability + full detail.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isEmr && (
            <>
              <label className="flex items-center gap-2 text-xs text-ink-muted">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                Show inactive
              </label>
              <button
                className="btn-secondary h-9 whitespace-nowrap"
                onClick={() => gridRef.current?.api.exportDataAsCsv({ fileName: 'providers.csv' })}
              >
                Export CSV
              </button>
              <button
                className="btn-primary h-9 whitespace-nowrap"
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                <Icon name="user-plus" size={14} /> New provider
              </button>
            </>
          )}
          {isEmr && (
            <>
              <button
                className="btn-secondary h-9 whitespace-nowrap"
                onClick={() => gridRef.current?.api.exportDataAsCsv({ fileName: 'emr-providers.csv' })}
              >
                Export CSV
              </button>
              <button
                className="btn-primary h-9 whitespace-nowrap"
                disabled={importEmr.isPending || selectedCount === 0}
                onClick={() => {
                  const selected = gridRef.current?.api.getSelectedRows() ?? [];
                  const ids = selected.map((r: any) => Number(r.id));
                  if (ids.length) importEmr.mutate(ids);
                }}
              >
                <Icon name="user-plus" size={14} />
                {importEmr.isPending
                  ? 'Importing…'
                  : `Import${selectedCount ? ` (${selectedCount})` : ''}`}
              </button>
            </>
          )}
        </div>
      </header>

      <div className="flex gap-1 border-b border-line">
        <button
          className={`px-4 py-2 text-sm font-medium ${tab === 'census' ? 'border-b-2 border-brand-600 text-brand-700' : 'text-ink-muted hover:text-ink'}`}
          onClick={() => setTab('census')}
        >
          Census Providers
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium ${tab === 'emr' ? 'border-b-2 border-brand-600 text-brand-700' : 'text-ink-muted hover:text-ink'}`}
          onClick={() => setTab('emr')}
        >
          EMR Providers
          {filteredEmrProviders.length ? (
            <span className="ml-1.5 rounded-full bg-ink-muted/10 px-1.5 py-0.5 text-xs text-ink-muted">
              {filteredEmrProviders.length}
            </span>
          ) : null}
        </button>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="relative w-full max-w-xs">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted">
            <Icon name="users" size={14} />
          </span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, role, email, service…"
            className="h-9 w-full rounded-md border border-line bg-white pl-9 pr-3 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          />
        </div>
        {isEmr && (
          <div className="flex h-9 items-center rounded-md border border-line bg-white text-xs font-medium overflow-hidden">
            {(['all', 'not-imported', 'imported'] as const).map((opt) => (
              <button
                key={opt}
                className={`px-3 h-full ${importFilter === opt ? 'bg-brand-600 text-white' : 'text-ink-muted hover:text-ink'}`}
                onClick={() => setImportFilter(opt)}
              >
                {opt === 'all' ? 'All' : opt === 'not-imported' ? 'Not imported' : 'Imported'}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="ag-theme-quartz flex-1 overflow-hidden rounded-card shadow-card ring-1 ring-line">
        <AgGridReact
          ref={gridRef}
          rowData={isEmr ? filteredEmrProviders : providers ?? []}
          columnDefs={isEmr ? emrColumnDefs : columnDefs}
          animateRows
          quickFilterText={search}
          pagination
          paginationPageSize={50}
          paginationPageSizeSelector={[25, 50, 100, 200]}
          singleClickEdit={!isEmr}
          stopEditingWhenCellsLoseFocus
          onCellValueChanged={isEmr ? undefined : onCellValueChanged}
          rowSelection={isEmr ? 'multiple' : undefined}
          suppressRowClickSelection={isEmr}
          onSelectionChanged={
            isEmr
              ? (e) => setSelectedCount(e.api.getSelectedRows().length)
              : undefined
          }
          loading={isEmr ? emrLoading : isLoading}
          overlayLoadingTemplate='<span class="text-sm text-slate-500">Loading providers…</span>'
          overlayNoRowsTemplate="No providers found"
        />
      </div>

      <ProviderForm open={formOpen} onClose={() => setFormOpen(false)} editing={editing} />
    </div>
  );
}
